import { useEffect, useState, useRef } from "react";
import { Upload, Play, Trash2, MonitorPlay, X, Loader2 } from "lucide-react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import FocusHeader from "@/components/FocusHeader";
import { TID } from "@/lib/testIds";
import { useRealtimeChannel } from "@/context/RealtimeContext";

export default function ScreenSaver() {
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [playing, setPlaying] = useState(false);
    const [currentIdx, setCurrentIdx] = useState(0);
    const fileInputRef = useRef(null);
    const videoRef = useRef(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/screensaver/videos");
            setVideos(data);
        } catch (e) {
            toast.error("Failed to load videos.");
        } finally {
            setLoading(false);
        }
    };

    useRealtimeChannel("screensaver", load);

    useEffect(() => {
        load();
    }, []);

    const upload = async (file) => {
        if (!file) return;
        if (file.size > 300 * 1024 * 1024) {
            toast.error("File exceeds 300 MB limit.");
            return;
        }
        setUploading(true);
        const form = new FormData();
        form.append("file", file);
        try {
            const { data } = await api.post("/screensaver/videos", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setVideos((prev) => [...prev, data]);
            toast.success(`${data.title} uploaded.`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Upload failed.");
        } finally {
            setUploading(false);
        }
    };

    const remove = async (v) => {
        try {
            await api.delete(`/screensaver/videos/${v.id}`);
            setVideos((prev) => prev.filter((x) => x.id !== v.id));
        } catch (e) {
            toast.error("Delete failed.");
        }
    };

    const activeVideos = videos.filter((v) => v.is_active !== false);

    const startPlayback = () => {
        if (activeVideos.length === 0) {
            toast.info("Upload at least one video first.");
            return;
        }
        setCurrentIdx(0);
        setPlaying(true);
    };

    const nextVideo = () => {
        setCurrentIdx((i) => (i + 1) % activeVideos.length);
    };

    const buildStreamUrl = (id) => {
        // Cookie-auth: browser attaches access_token to same-origin <video src=…>.
        return `${API}/screensaver/videos/${id}/stream`;
    };

    // Detect image vs video for mixed-media playback. Uses stored media_kind
    // when present, falls back to content_type sniff, else assumes video.
    const kindOf = (v) => {
        if (!v) return "video";
        if (v.media_kind) return v.media_kind;
        if (v.content_type?.startsWith("image/")) return "image";
        return "video";
    };

    // Image hold: 15 seconds each. Videos advance via <video onEnded>.
    const holdTimerRef = useRef(null);
    useEffect(() => {
        if (holdTimerRef.current) {
            clearTimeout(holdTimerRef.current);
            holdTimerRef.current = null;
        }
        if (!playing) return;
        const current = activeVideos[currentIdx];
        if (kindOf(current) !== "image") return;
        holdTimerRef.current = setTimeout(() => nextVideo(), 15000);
        return () => {
            if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
            holdTimerRef.current = null;
        };
    }, [playing, currentIdx, activeVideos]);

    // Playback overlay
    if (playing) {
        const current = activeVideos[currentIdx];
        const currentKind = kindOf(current);
        return (
            <div className="fixed inset-0 z-[90] bg-black">
                {current && currentKind === "image" ? (
                    <img
                        key={current.id}
                        src={buildStreamUrl(current.id)}
                        alt={current.title}
                        onError={() => {
                            toast.error("Image failed to load.");
                            nextVideo();
                        }}
                        className="w-full h-full object-cover"
                        data-testid="screensaver-image-frame"
                    />
                ) : current ? (
                    <video
                        key={current.id}
                        ref={videoRef}
                        src={buildStreamUrl(current.id)}
                        autoPlay
                        muted
                        onEnded={nextVideo}
                        onError={() => {
                            toast.error("Playback error.");
                            nextVideo();
                        }}
                        className="w-full h-full object-cover"
                    />
                ) : null}
                {/* HUD overlay for visitor mode */}
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute top-6 left-6 flex items-center gap-4">
                        <img
                            src="/logos/virtus-white.png"
                            alt="VIRTUS"
                            className="h-12 w-auto drop-shadow-[0_0_12px_rgba(255,85,0,0.35)]"
                        />
                        <div className="h-10 w-px bg-white/30" />
                        <div>
                            <div className="font-mono text-[11px] tracking-[0.32em] uppercase text-virtus-orange">
                                VIRTUS AI
                            </div>
                            <div className="font-mono text-[9px] tracking-[0.24em] uppercase text-white/70">
                                SYSTEM ACTIVE
                            </div>
                        </div>
                    </div>
                    <div className="absolute bottom-6 left-6 font-mono text-[10px] tracking-[0.24em] uppercase text-white/70">
                        {current?.title} · {currentIdx + 1} / {activeVideos.length}
                    </div>
                    <img
                        src="/logos/virtus-maritime-white.png"
                        alt="VIRTUS MARITIME"
                        className="absolute bottom-6 right-24 h-14 w-auto opacity-60"
                    />
                </div>
                <button
                    onClick={() => setPlaying(false)}
                    className="absolute top-6 right-6 flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase bg-black/60 text-white border border-white/40 px-3 py-2 hover:bg-virtus-stuck hover:border-virtus-stuck"
                    data-testid={TID.screenSaverExitBtn}
                >
                    <X size={14} /> EXIT
                </button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-virtus-bg text-white">
            <FocusHeader
                code="AO/04"
                title="Screen Saver"
                subtitle="Product showcase for visitors. Full-bleed autoplay."
            />

            <div className="p-8">
                <div className="flex items-center justify-between mb-6">
                    <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim">
                        MEDIA VAULT // {videos.length} ITEM{videos.length === 1 ? "" : "S"}
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange border border-virtus-orange px-4 py-2 hover:bg-virtus-orange hover:text-black disabled:opacity-50"
                            data-testid={TID.screenSaverUploadBtn}
                        >
                            {uploading ? (
                                <>
                                    <Loader2 size={12} className="animate-spin" /> UPLOADING
                                </>
                            ) : (
                                <>
                                    <Upload size={12} /> UPLOAD MEDIA
                                </>
                            )}
                        </button>
                        <button
                            onClick={startPlayback}
                            disabled={activeVideos.length === 0}
                            className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase bg-virtus-orange text-black px-4 py-2 hover:bg-white glow-orange disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid={TID.screenSaverPlayBtn}
                        >
                            <MonitorPlay size={12} /> LAUNCH SHOWCASE
                        </button>
                    </div>
                </div>

                <input
                    ref={fileInputRef}
                    type="file"
                    accept="video/mp4,video/quicktime,video/webm,video/x-m4v,image/jpeg,image/png,image/webp,image/gif"
                    className="hidden"
                    onChange={(e) => upload(e.target.files?.[0])}
                    data-testid={TID.screenSaverFileInput}
                />

                {loading ? (
                    <div className="py-24 flex items-center justify-center text-virtus-dim">
                        <Loader2 className="animate-spin" />
                    </div>
                ) : videos.length === 0 ? (
                    <div className="border border-dashed border-virtus-line p-24 text-center">
                        <MonitorPlay
                            size={48}
                            className="mx-auto text-virtus-dim mb-4"
                        />
                        <div className="font-heading text-xl uppercase tracking-tight text-white mb-2">
                            No media uploaded
                        </div>
                        <div className="text-sm text-virtus-dim mb-6">
                            Upload videos (mp4/mov/webm, up to 300 MB) or images (jpg/png/webp/gif,
                            up to 30 MB). Images hold 15 seconds each in the slideshow.
                        </div>
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange border border-virtus-orange px-4 py-2 hover:bg-virtus-orange hover:text-black"
                        >
                            <Upload size={12} className="inline mr-2" /> UPLOAD FIRST ITEM
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {videos.map((v) => {
                            const k = kindOf(v);
                            return (
                                <div
                                    key={v.id}
                                    className="border border-virtus-line bg-virtus-surface"
                                    data-testid={TID.screenSaverVideoRow(v.id)}
                                >
                                    <div className="aspect-video bg-black flex items-center justify-center relative">
                                        {k === "image" ? (
                                            <img
                                                src={buildStreamUrl(v.id)}
                                                alt={v.title}
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <video
                                                src={buildStreamUrl(v.id)}
                                                muted
                                                preload="metadata"
                                                className="w-full h-full object-cover"
                                            />
                                        )}
                                        <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                                            <div className="w-12 h-12 border border-virtus-orange flex items-center justify-center">
                                                <Play size={20} className="text-virtus-orange" />
                                            </div>
                                        </div>
                                        <div className="absolute top-2 left-2 px-2 py-0.5 bg-black/70 font-mono text-[9px] tracking-[0.24em] uppercase text-virtus-orange border border-virtus-orange/40">
                                            {k === "image" ? "IMG · 15s" : "VIDEO"}
                                        </div>
                                    </div>
                                    <div className="px-4 py-3 border-t border-virtus-line flex items-center justify-between gap-3">
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm text-white truncate">
                                                {v.title}
                                            </div>
                                            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-faint">
                                                {v.size ? `${(v.size / 1024 / 1024).toFixed(1)} MB` : "PRELOAD"}
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => remove(v)}
                                            className="text-virtus-faint hover:text-virtus-stuck"
                                            aria-label="Delete"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
